import {Component, ElementRef, EventEmitter, Input, Output, SecurityContext} from '@angular/core';
import { resumeAnimation } from '../animation';
import {
  ContactItem,
  DocListItem, FinancialAgreementCreditor,
  FinancialAgreementDiscount, FinancialAgreementDocument,
  FinancialAgreementFee,
  SolrInput
} from '@secca/shared/models/SupplierSearchResponse';
import { ProviderOwnershipEnum } from '@secca/shared/models/enums';
import {
  SupplierSearchHelper
} from '@secca/case/components/case-plans/case-plans/add-service-order/search-service-provider/search-service-provider-modal/supplier-search-helper.service';
import { DomSanitizer } from '@angular/platform-browser';
import {SupplierService} from '@secca/core/services/supplier.service';

@Component({
    selector: 'app-supplier-info',
    templateUrl: './supplier-info.component.html',
    styleUrls: ['./supplier-info.component.scss'],
    animations: resumeAnimation,
    standalone: false
})
export class SupplierInfoComponent {
  @Input()
  supplier: DocListItem = {} as DocListItem;
  @Input()
  solrInput: SolrInput;
  @Output()
  changeSupplierElect = new EventEmitter<string>();
  @Input()
  private openGeneral = false;
  private openFinancialAgreements = false;
  private supplierOnChain: DocListItem = undefined;

  allowSelection: boolean;
  amountOfCountriesToShow = 15;

  @Input()
  set shouldAllowSelection(value: boolean) {
    this.allowSelection = value;
  }

  @Input()
  set shouldExpandGeneral(value: boolean) {
    this.openGeneral = value;
  }

  @Input()
  set shouldExpandFinancialAgreements(value: boolean) {
    this.openFinancialAgreements = value;
  }

  constructor(protected supplierSearchHelper: SupplierSearchHelper,
              private sanitizer: DomSanitizer,
              private supplierInfoElement: ElementRef<HTMLElement>,
              private supplierService: SupplierService) {
  }

  select(supplierInfoComponent: SupplierInfoComponent) {
    this.changeSupplierElect.emit(supplierInfoComponent.supplier.id);
  }

  public hasFinancialAgreement(): boolean {

    return true;
  }

  public toggleGeneral(): void {
    this.openGeneral = !this.openGeneral;
  }

  public toggleFinancialAgreements(): void {
    if (this.openFinancialAgreements === false &&
      this.supplierOnChain === undefined &&
      this.supplier.parentaccountValue !== undefined) {
      this.supplierService.getSupplierFromAccount(this.supplier.parentaccountValue).subscribe(response => {
        if (response.docList.length > 0) {
          this.supplierOnChain = response.docList[0];
        }
      });
    }
    this.openFinancialAgreements = !this.openFinancialAgreements;
  }

  public isGeneralOpen(): boolean {
    return this.openGeneral;
  }

  public isFinancialAgreementsOpen(): boolean {
    return this.openFinancialAgreements;
  }

  public isGeneralOpenState(): string {
    return this.isGeneralOpen() ? 'open' : 'close';
  }

  public isFinancialAgreementsOpenState(): string {
    return this.isFinancialAgreementsOpen() ? 'open' : 'close';
  }

  private cleanHTML(input: string): string {
    if (!input) {
      return;
    }
    // remove non html tags
    let output = input.replace(/<\/?[^>]+(>|$)/g, (match) => {
      return match.trim() === '<p>' ||
      match.trim() === '</p>' ||
      match.trim() === '<br />' ||
      match.trim() === '<li>' ||
      match.trim() === '</li>' ||
      /<strong>.*<\/strong>/.test(match) ? match : '';
    });
    // remove empty p tags
    output = output.replace(/<p>\s*<\/p>/g, '');
    return output;
  }

  private linebreaksToHtml(input: string): string {
    return input.replace(/(\r\n|\n|\r)/g, '<br />');
  }

  getDescriptionAsHtml(description: string): any {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.cleanHTML(description));
  }

  getHospitalIconPrivate(privateCompany: string) {
    return privateCompany === (ProviderOwnershipEnum.Private || ProviderOwnershipEnum.Both) ? '/assets/icons/provider/hosp_private_true_24x24.svg' : '/assets/icons/provider/hosp_private_false_24x24.svg';
  }

  getHospitalIconPublic(publicCompany: string) {
    return publicCompany === (ProviderOwnershipEnum.Public || ProviderOwnershipEnum.Both) ? '/assets/icons/provider/hosp_public_true_24x24.svg' : '/assets/icons/provider/hosp_public_false_24x24.svg';
  }

  filterOnContact(contactList: ContactItem[], contactType: string) {
    return contactList.filter(contact => contact.contact_type === contactType);
  }

  distinctContactTypes(contactList: ContactItem[]): string[] {
    return contactList.map(contact => contact.contact_type).filter(this.uniqueFilter);
  }

  uniqueFilter(value, index, self) {
    return self.indexOf(value) === index;
  }

  getSupplierScore(recommendations: string[]): { iconPath: string, tooltip: string } {
    return this.supplierSearchHelper.setIconSupplierLevel(recommendations);
  }

  getRecommendationDecor(recommendation: string): string {
    return this.supplierSearchHelper.getRecommendationDecor(recommendation);
  }

  getBlacklistFinancialMedicalScore(recommendations: string[]): { iconPath: string, tooltip: string } {
    return this.supplierSearchHelper.setIconFinancialMedicalOrBlackListed(recommendations);
  }

  public scrollToElement(selector: string) {
    setTimeout(() => {
      const element = (this.supplierInfoElement.nativeElement as HTMLElement).querySelector(selector);

      if (!!element) {
        element.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
      }
    });
  }

  getFeeRemarks(): string {
    return (this.supplier?.remark_list?.[0]?.remark_fee ?? '');
  }

  getFeeRemarksAsHtml(): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.cleanHTML(this.linebreaksToHtml(this.getFeeRemarks())));
  }

  getDiscountRemarks(): string {
    return this.supplier?.remark_list?.[0]?.remark_discount ?? '';
  }

  getDiscountRemarksAsHtml(): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.cleanHTML(this.linebreaksToHtml(this.getDiscountRemarks())));
  }

  getParentAccountName(): string {
    return this.supplier?.parentaccountName ?? '';
  }

  getFeesOnSupplier(): FinancialAgreementFee[] {
    return this.supplier?.fee_list ?? [{
      fee_type: '',
      fee_price: 0,
      fee_priceFormatted: '',
      fee_invoiceFeeName: '',
      fee_contractFeeName: '\u00A0', // Prevent table from collapsing row
      fee_currencyIsoCode: '',
      fee_validFromDate: '',
      fee_validToDate: ''}];
  }

  getFeesOnChain(): FinancialAgreementFee[] {
    return this.supplierOnChain?.fee_list ?? [{
      fee_type: '',
      fee_price: 0,
      fee_priceFormatted: '',
      fee_invoiceFeeName: '',
      fee_contractFeeName: '\u00A0', // Prevent table from collapsing row
      fee_currencyIsoCode: '',
      fee_validFromDate: '',
      fee_validToDate: ''}];
  }

  getDiscountsOnSupplier(): FinancialAgreementDiscount[] {
    return this.supplier?.discount_list ?? [{
      discount_discountRemark: '',
      discount_percentageOfDiscount: 0,
      discount_percentageOfDiscountFormatted: '',
      discount_allAmount: '',
      discount_expectedOnBilling: '',
      discount_name: '\u00A0',
      discount_validFromDate: '',
      discount_validToDate: '',
    }];
  }

  getDiscountsOnChain(): FinancialAgreementDiscount[] {
    return this.supplierOnChain?.discount_list ?? [{
      discount_discountRemark: '',
      discount_percentageOfDiscount: 0,
      discount_percentageOfDiscountFormatted: '',
      discount_allAmount: '',
      discount_expectedOnBilling: '',
      discount_name: '\u00A0',
      discount_validFromDate: '',
      discount_validToDate: '',
    }];
  }

  getDocumentsOnSupplier(): FinancialAgreementDocument[] {
    const list = this.supplier?.document_list?.filter(doc => doc.document_visibleTo !== 'Network only') ?? [];
    return list.length > 0 ? list : [{
      document_description: '',
      document_documentUrl: '',
      document_name: '\u00A0',
      document_supplier: '',
      document_type: '',
      document_visibleTo: '',
      document_validFromDate: '',
      document_validToDate: '',
    }];
  }

  getDocumentsOnChain(): FinancialAgreementDocument[] {
    const list = this.supplierOnChain?.document_list?.filter(doc => doc.document_visibleTo !== 'Network only') ?? [];
    return list.length > 0 ? list : [{
      document_description: '',
      document_documentUrl: '',
      document_name: '\u00A0',
      document_supplier: '',
      document_type: '',
      document_visibleTo: '',
      document_validFromDate: '',
      document_validToDate: '',
    }];
  }

  getCreditorsOnSupplier(): FinancialAgreementCreditor[] {
    return this.supplier?.creditor_list ?? [{
      creditor_type: '',
      creditor_autoApproval: '',
      creditor_name: '\u00A0',
      creditor_alias: '',
      creditor_creditorNumber: '',
      creditor_currency: '',
      creditor_accountNumber: '',
      creditor_swift: '',
    }];
  }

  getCreditorsOnChain(): FinancialAgreementCreditor[] {
    return this.supplierOnChain?.creditor_list ?? [{
      creditor_type: '',
      creditor_autoApproval: '',
      creditor_name: '\u00A0',
      creditor_alias: '',
      creditor_creditorNumber: '',
      creditor_currency: '',
      creditor_accountNumber: '',
      creditor_swift: '',
    }];
  }
}
